import { useQueryClient, useMutation } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services";

import { LayoutRecord, UpdateLayoutRequest } from "../LayoutRecord";

import { layoutQueryKeys } from "./layoutQueryKeys";

export function useUpdateLayout() {
  const { layoutService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      layoutId: string;
      updates: UpdateLayoutRequest;
    }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return layoutService.updateLayout(
        request.layoutId,
        request.updates,
        currentOrganization.org_id,
      );
    },
    onSuccess: (layoutRecord) => {
      queryClient.setQueryData(
        layoutQueryKeys.id(layoutRecord.layout_id),
        layoutRecord,
      );
      queryClient.setQueryData<PaginatedResponse<LayoutRecord>>(
        layoutQueryKeys.lists(),
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          return {
            ...oldData,
            items: oldData.items.map((record) =>
              record.layout_id === layoutRecord.layout_id
                ? layoutRecord
                : record,
            ),
          };
        },
      );
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error);
    },
  });
}
